import { TYPES_LANGUAGES } from './constants/languages'
import { LOGOUT_REDIRECT_URL } from './constants/localStorage'
import i18n from 'config/i18n'
import { queryClient } from 'config/reactQuery'
import { USER_KEYS } from 'hooks/user'
import {
  DASH_STRING,
  EMPTY_STRING,
  PUBLISHERS,
  TYPES_COUNTRIES,
  TYPES_DEVICES,
  TYPES_USER_ROLES,
  UNDERSCORE_STRING,
} from 'utils/constants'

export const emailIsCorrect = (email) => {
  const pattern =
    /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/gi
  const isCorrect = email?.match(pattern) !== null
  return isCorrect
}

export const checkIsCanaryUser = (id) => window?.def?.canaryUsers?.includes(id)
export const checkIsDemoCanaryUser = (id) =>
  window?.def?.demoCanaryUsers?.includes(id)

export const checkIsFilterEvaluationsCanaryUser = (id) =>
  window?.filtersEvaluations?.canaryUsers?.includes(id)

export const checkIsDemoCalificationsCanaryUser = (id) =>
  window?.calificationsSystem?.demo?.canaryUsers?.includes(id)

export const checkDuarteUpdatesCanaryUser = (id) =>
  window?.duarteUpdates?.canaryUsers?.includes(id)

export const convertBase100ToBase10 = ({ base100 }) =>
  Math.round(base100 * 10) / 100

export const convertBase10ToBase100 = ({ base10 }) =>
  Math.round(base10 * 100) / 10

export const deprecatedConvertTimeStampToDate = ({ timestamp }) => {
  const date = new Date(timestamp * 1000)
  const year = date.getFullYear()
  const month = date.getMonth() + 1
  const day = date.getDate()
  const hour = date.getHours()
  const minutes =
    date.getMinutes() < 10 ? `0${date.getMinutes()}` : date.getMinutes()
  const seconds = date.getSeconds()

  return {
    day,
    hour,
    minutes,
    month,
    seconds,
    year,
  }
}

const DEFAULT_LANGUAGE = navigator.language
const DEFAULT_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone
const DEFAULT_OPTIONS = {
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  month: '2-digit',
  year: 'numeric',
}
export const convertTimeStampToDate = ({
  language = DEFAULT_LANGUAGE,
  options = DEFAULT_OPTIONS,
  timestamp,
  timezone = DEFAULT_TIMEZONE,
}) => {
  if (!timestamp) throw new Error('The timestamp is not defined')
  const currentDate = new Date(timestamp * 1000)
  if (Number.isNaN(currentDate.getTime())) {
    return 'Invalid Date'
  }
  const toBCP47 = (locale) => {
    return locale.replace(UNDERSCORE_STRING, DASH_STRING)
  }
  const locale = toBCP47(language)
  const formatOptions = {
    ...options,
    timeZone: timezone,
  }
  return new Intl.DateTimeFormat(locale, formatOptions).format(currentDate)
}

export const getDatePartsFromTimestamp = ({ language, timestamp }) => {
  if (!timestamp) throw new Error('The timestamp is not defined')
  const currentDate = new Date(timestamp * 1000)
  const dateConfig = {
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }
  const toBCP47 = (locale) => {
    return locale.replace(UNDERSCORE_STRING, DASH_STRING)
  }
  const getDateParts = ({ date, language }) => {
    const formattedDate = new Intl.DateTimeFormat(
      toBCP47(language),
      dateConfig
    ).formatToParts(date)
    return Object.fromEntries(formattedDate.map((p) => [p.type, p.value]))
  }
  try {
    return getDateParts({ date: currentDate, language })
  } catch {
    return getDateParts({ date: currentDate, language: TYPES_LANGUAGES.ES_ES })
  }
}

export const dateToTimestamp = (dateString) => {
  const parts = dateString.split(/[/ :]/)
  const [day, month, year, hour, minute] = parts.map(Number)
  const date = new Date(year, month - 1, day, hour, minute)
  return Math.floor(date.getTime() / 1000)
}

export const fillTwoDigits = (value) => {
  return value.toString().padStart(2, '0')
}

export const getUrlParams = (paramName) => {
  const queryString = window.location.search
  const urlParams = new URLSearchParams(queryString)
  const params = urlParams?.get(paramName)
  return params
}

const BASE_FONT_SIZE = 16
export const pxToRem = (px) => `${px / BASE_FONT_SIZE}rem`

export const isString = (value) =>
  typeof value === 'string' || value instanceof String

export const checkError = (errors) => {
  const keys = Object.keys(errors)
  const values = Object.values(errors)
  const hasErrors = values.reduce(
    (arr, val, index) => (val && arr) || [...arr, keys[index]],
    []
  )

  hasErrors.forEach((element) => {
    const error = `prop ${element} is required`
    console.error(error)
    throw error
  })
}

export const getCurrentUserTypeByRole = (role) => {
  const isRelative = role === TYPES_USER_ROLES.RELATIVE
  const isStudent = [
    TYPES_USER_ROLES.STUDENT,
    TYPES_USER_ROLES.STUDENT_UNDER_14,
  ].includes(role)
  const isStudentOver14 = role === TYPES_USER_ROLES.STUDENT
  const isStudentunder14 = role === TYPES_USER_ROLES.STUDENT_UNDER_14
  const isTeacher = role === TYPES_USER_ROLES.TEACHER
  const isAdmin = role === TYPES_USER_ROLES.ADMIN
  return {
    isAdmin,
    isRelative,
    isStudent,
    isStudentOver14,
    isStudentunder14,
    isTeacher,
  }
}

export const transformPositionIndexInHumanFormat = (index) => index + 1

export const isNumber = (number) => number || number === 0

export const autoReplace = (text, correction) => {
  const reg = new RegExp(Object.keys(correction).join('|'), 'g')
  return text.replace(reg, (matched) => correction[matched])
}

export const formatCurseName = ({ course }) => {
  const { school } = queryClient.getQueryData([USER_KEYS.GET_USER]) || {}

  if (!course) return EMPTY_STRING

  const currentCourse = i18n.t(course?.name) ?? EMPTY_STRING
  const level = i18n.t(course?.level?.name) ?? EMPTY_STRING
  let fullName = `${currentCourse} ${level}`.trim()
  const isNewFormatCourse = ['GT', 'ES', 'CR', 'HN', 'SI'].includes(
    school?.isoCountry
  )

  if (isNewFormatCourse) {
    // fullName = i18n.t(course?.translationKey) ?? EMPTY_STRING
    fullName = i18n.t(`common.${course?.translationKey}`) ?? EMPTY_STRING
  }

  return fullName
}

export const formatClassroomName = ({ classroom }) => {
  if (!classroom) return EMPTY_STRING

  const courseName = formatCurseName({ course: classroom?.course })
  const group = classroom?.name ?? EMPTY_STRING
  const fullName = `${courseName} ${group}`.trim()

  return fullName
}

export const deepCopy = (array) => JSON.parse(JSON.stringify(array))

export const checkSpecialCharacters = (value) => {
  const regex = new RegExp('[^a-zA-Z0-9 \\-\\.\\_]+')
  return regex.test(value)
}

export const checkDeviceIsTabletOrMobile = () => {
  const LOCAL_STORAGE_VALUE = { KEY: 'messageDownload', VALUE: 1 }
  const patternIOS = /iPhone|iPad|iPod/
  const patternANDROID = /Android/
  const maxAttempts = 5
  const {
    localStorage,
    navigator: { userAgent },
  } = window
  const isIos = userAgent.match(patternIOS)
  const isAndroid = userAgent.match(patternANDROID)
  const checkMessageLocalStorage = (device) => {
    const hasMessage = localStorage.getItem(LOCAL_STORAGE_VALUE.KEY)
    if (hasMessage && hasMessage <= maxAttempts) {
      return device
    }
    if (!hasMessage) {
      localStorage.setItem(LOCAL_STORAGE_VALUE.KEY, LOCAL_STORAGE_VALUE.VALUE)
    }
  }
  if (isIos) {
    return checkMessageLocalStorage(TYPES_DEVICES.IOS)
  }
  if (isAndroid) {
    return checkMessageLocalStorage(TYPES_DEVICES.ANDROID)
  }
}

export const covertBookTitle = (textList) => textList.filter(Boolean).join(' ')

export const isMladinskaPublisher = (publishers) =>
  publishers?.length === 1 && publishers[0]?.name === PUBLISHERS.MLADINSKA

export const goToLoginPage = ({
  hasreferal = false,
  language = TYPES_LANGUAGES.ES_ES,
} = {}) => {
  if (process.env.NODE_ENV !== 'development') {
    const { publishers = [] } =
      queryClient.getQueryData({
        queryKey: [USER_KEYS.GET_USER],
      }) || {}
    const providerParam = getUrlParams('provider')
    const localLogoutUrlRedirectStored =
      localStorage.getItem(LOGOUT_REDIRECT_URL)
    const isMladinska =
      isMladinskaPublisher(publishers) ||
      providerParam === PUBLISHERS.MLADINSKA ||
      localLogoutUrlRedirectStored === window.def.mladinskaPublicUrl
    const referal = hasreferal
      ? `?rf=${encodeURIComponent(window.location)}`
      : ''

    window.location.href = isMladinska
      ? `${window.def.mladinskaPublicUrl}`
      : `${window.origin}/${language}/login${referal}`
  } else {
    console.info('success logout')
  }
}

export const getStatusInput = ({ isError = false, isSuccess = false }) => {
  if (isError) {
    return 'error'
  }
  if (isSuccess) {
    return 'success'
  }
  return 'default'
}

export const formatLocaleToBasicLanguage = (locale) => locale.split('_')[0]

export const sortObject = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map(sortObject)
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj)
      .sort()
      .reduce((sorted, key) => {
        sorted[key] = sortObject(obj[key])
        return sorted
      }, {})
  } else {
    return obj
  }
}

export const formatBookAutonomies = (book) => {
  if (book?.autonomies?.length && book?.isoCountry === TYPES_COUNTRIES.ES) {
    return book.autonomies
      .map((region) => region?.name)
      .filter(Boolean)
      .join(', ')
  }
  return ''
}
